import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { franchisesAPI, cardsAPI } from '../services/api';
import TranslationTable from '../components/TranslationTable';
import { toast } from 'react-toastify';
import './TranslationPage.css';

const TranslationPage = () => {
  // États pour les combos
  const [franchises, setFranchises] = useState([]);
  const [languages, setLanguages] = useState([]); // Liste complète des langues disponibles
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [selectedLanguages, setSelectedLanguages] = useState([]); // Array de codes: ['en', 'es', 'it']

  // États pour les traductions (par langue)
  const [translationsByLang, setTranslationsByLang] = useState({}); // { en: [...], es: [...], it: [...] }
  const [cardUuids, setCardUuids] = useState([]);
  const [statsByLang, setStatsByLang] = useState({}); // { en: { localWords: 0, deeplWords: 0 }, ... }
  const [activeSaleSupports, setActiveSaleSupports] = useState([]);

  // États pour le workflow
  const [translating, setTranslating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [progress, setProgress] = useState({ phase: '', current: 0, total: 0, message: '' });
  const [canValidate, setCanValidate] = useState(false);
  const [selectedTabLang, setSelectedTabLang] = useState(null); // Langue active dans les tabs
  const [showValidationDialog, setShowValidationDialog] = useState(false); // Dialog de confirmation

  // Charger les franchises au démarrage
  useEffect(() => {
    loadFranchises();
  }, []);

  // Charger les langues quand une franchise est sélectionnée
  useEffect(() => {
    if (selectedFranchise) {
      loadLanguages(selectedFranchise.schema);
    }
  }, [selectedFranchise]);

  const loadFranchises = async () => {
    try {
      const response = await franchisesAPI.getAll();
      const options = response.data.franchises.map(f => ({
        value: f.id,
        label: f.nom,
        schema: f.schema
      }));
      setFranchises(options);
    } catch (error) {
      console.error('Erreur lors du chargement des franchises:', error);
      toast.error('Erreur lors du chargement des franchises');
    }
  };

  const loadLanguages = async (schema) => {
    try {
      const response = await franchisesAPI.getLanguages(schema);
      const langs = response.data.languages.map(l => ({
        code: l.code,
        label: l.label
      }));
      setLanguages(langs);
    } catch (error) {
      console.error('Erreur lors du chargement des langues:', error);
      toast.error('Erreur lors du chargement des langues');
    }
  };

  // Gérer la sélection/désélection des langues
  const handleLanguageToggle = (langCode) => {
    setSelectedLanguages(prev => {
      if (prev.includes(langCode)) {
        return prev.filter(l => l !== langCode);
      } else {
        return [...prev, langCode];
      }
    });
  };

  const handleTranslate = async () => {
    if (!selectedFranchise || selectedLanguages.length === 0) {
      toast.warning('Veuillez sélectionner une franchise et au moins une langue');
      return;
    }

    try {
      // Afficher immédiatement un toast de chargement
      toast.info('⏳ Chargement des cartes depuis la base de données...', {
        autoClose: false,
        toastId: 'loading-cards'
      });

      setTranslating(true);
      setCanValidate(false);
      setProgress({ phase: '', current: 0, total: 0, message: '' });
      setTranslationsByLang({});
      setCardUuids([]);
      setStatsByLang({});
      setActiveSaleSupports([]);
      setSelectedTabLang(null);

      const progressCallback = (data) => {
        console.log('Progress:', data);

        if (data.type === 'extraction_start') {
          // Fermer le toast de chargement
          toast.dismiss('loading-cards');
          toast.info(`🔍 Extraction des expressions depuis ${data.totalCards} cartes...`, {
            autoClose: false,
            toastId: 'extraction'
          });
          setProgress({
            phase: 'extraction',
            current: 0,
            total: data.totalCards,
            message: `Extraction des expressions depuis ${data.totalCards} cartes...`
          });
          setActiveSaleSupports(data.activeSaleSupports || []);
        } else if (data.type === 'extraction_complete') {
          toast.dismiss('extraction');
          toast.success(`✓ ${data.totalExpressions} expressions trouvées (${data.totalWords} mots)`, {
            autoClose: 3000
          });
          setProgress({
            phase: 'extraction',
            current: data.totalExpressions,
            total: data.totalExpressions,
            message: `✓ ${data.totalExpressions} expressions (${data.totalWords} mots) - ${data.totalOperations} traductions à effectuer`
          });
        } else if (data.type === 'translation_item') {
          console.log(`[FRONTEND] ========== TRANSLATION ITEM REÇU ==========`);
          console.log(`[FRONTEND] Langue: ${data.lang}`);
          console.log(`[FRONTEND] Expression: ${data.translation.expressionOrigine}`);
          console.log(`[FRONTEND] Traduction: ${data.translation.expressionTraduite}`);

          // Ajouter la traduction au fur et à mesure dans le state
          setTranslationsByLang(prev => {
            console.log(`[FRONTEND] État AVANT ajout:`, Object.keys(prev).map(k => `${k}: ${prev[k]?.length || 0} items`));

            const updated = {
              ...prev,
              [data.lang]: [...(prev[data.lang] || []), data.translation]
            };

            console.log(`[FRONTEND] État APRÈS ajout:`, Object.keys(updated).map(k => `${k}: ${updated[k].length} items`));
            console.log(`[FRONTEND] ===============================================\n`);
            return updated;
          });
        } else if (data.type === 'translation_progress') {
          if (data.phase === 'translation') {
            // Toast de progression uniquement au début de la traduction
            if (data.current === 1) {
              toast.info(`🌐 Traduction vers ${selectedLanguages.map(l => l.toUpperCase()).join(', ')}...`, {
                autoClose: false,
                toastId: 'translation'
              });
            }
            setProgress({
              phase: 'translation',
              current: data.current,
              total: data.total,
              message: `Traduction: ${data.current}/${data.total} (${data.targetLang.toUpperCase()})`
            });
            if (data.stats) {
              setStatsByLang(data.stats);
            }
          }
        }
      };

      const result = await cardsAPI.translate(
        selectedFranchise.schema,
        selectedLanguages, // Array de langues
        progressCallback
      );

      if (result) {
        // Fermer le toast de traduction
        toast.dismiss('translation');
        toast.success('✓ Traductions terminées avec succès!', {
          autoClose: 3000
        });

        console.log('\n╔══════════════════════════════════════════════════════════╗');
        console.log('║        RÉSULTAT FINAL DE LA TRADUCTION                  ║');
        console.log('╚══════════════════════════════════════════════════════════╝');
        console.log('[FRONTEND] Langues sélectionnées par l\'utilisateur:', selectedLanguages);
        console.log('[FRONTEND] Langues dans result.translationsByLang:', Object.keys(result.translationsByLang));

        Object.keys(result.translationsByLang).forEach(lang => {
          console.log(`  ${lang.toUpperCase()}: ${result.translationsByLang[lang].length} traductions`);
          if (result.translationsByLang[lang].length > 0) {
            console.log(`    Première traduction: ${result.translationsByLang[lang][0].expressionOrigine} → ${result.translationsByLang[lang][0].expressionTraduite}`);
          }
        });

        // Vérifier si toutes les langues sélectionnées sont présentes
        const missingLangs = selectedLanguages.filter(lang => !result.translationsByLang[lang] || result.translationsByLang[lang].length === 0);
        if (missingLangs.length > 0) {
          console.error('[FRONTEND] ⚠️ LANGUES MANQUANTES dans le résultat final:', missingLangs);
        } else {
          console.log('[FRONTEND] ✓ Toutes les langues sélectionnées sont présentes');
        }
        console.log('══════════════════════════════════════════════════════════\n');

        setTranslationsByLang(result.translationsByLang);
        setCardUuids(result.cards.map(c => c.iuud));
        setStatsByLang(result.statsByLang);
        setActiveSaleSupports(result.activeSaleSupports);
        setCanValidate(true);
        setSelectedTabLang(selectedLanguages[0]); // Sélectionner la première langue

        const totalTranslations = Object.values(result.translationsByLang).reduce(
          (sum, arr) => sum + arr.length,
          0
        );

        console.log(`[FRONTEND] Total traductions reçues: ${totalTranslations}`);
        console.log(`[FRONTEND] Détail par langue:`,
          Object.keys(result.translationsByLang).map(k => `${k}: ${result.translationsByLang[k].length} items`)
        );

        setProgress({
          phase: 'complete',
          current: totalTranslations,
          total: totalTranslations,
          message: `✓ Traduction terminée pour ${selectedLanguages.length} langue(s)`
        });
      }

    } catch (error) {
      console.error('Erreur lors de la traduction:', error);
      // Fermer tous les toasts de progression
      toast.dismiss('loading-cards');
      toast.dismiss('extraction');
      toast.dismiss('translation');
      toast.error('Erreur lors de la traduction: ' + (error.message || 'Erreur inconnue'), {
        autoClose: 5000
      });
      setProgress({
        phase: 'error',
        message: 'Erreur lors de la traduction'
      });
    } finally {
      setTranslating(false);
    }
  };

  const handleValidate = () => {
    const totalTranslations = Object.values(translationsByLang).reduce(
      (sum, arr) => sum + arr.length,
      0
    );

    if (totalTranslations === 0) {
      toast.warning('Aucune traduction à valider');
      return;
    }

    // Afficher le dialogue personnalisé
    setShowValidationDialog(true);
  };

  const handleCancelValidation = () => {
    setShowValidationDialog(false);
  };

  const handleConfirmValidation = () => {
    setShowValidationDialog(false);
    const totalTranslations = Object.values(translationsByLang).reduce(
      (sum, arr) => sum + arr.length,
      0
    );
    performValidation(totalTranslations);
  };

  const performValidation = async (totalTranslations) => {
    try {
      toast.info('📝 Application des traductions aux cartes...', {
        autoClose: false,
        toastId: 'validation'
      });

      setValidating(true);
      setProgress({ phase: '', current: 0, total: 0, message: '' });

      const progressCallback = (data) => {
        console.log('Validation progress:', data);

        if (data.type === 'phase') {
          setProgress({
            phase: data.phase,
            current: 0,
            total: 0,
            message: data.message
          });
        } else if (data.type === 'progress') {
          if (data.phase === 'applying_translations' && data.message) {
            setProgress({
              phase: data.phase,
              current: data.current,
              total: data.total,
              message: data.message
            });
          } else {
            setProgress({
              phase: data.phase,
              current: data.current,
              total: data.total,
              message: `${data.lang ? data.lang.toUpperCase() + ' - ' : ''}${data.phase}: ${data.current}/${data.total}`
            });
          }
        } else if (data.type === 'cache_complete') {
          let msg = 'Cache mis à jour:\n';
          Object.keys(data.statsByLang).forEach(lang => {
            const stats = data.statsByLang[lang];
            msg += `${lang.toUpperCase()}: ${stats.insertedCount} ins, ${stats.updatedCount} upd, ${stats.skippedCount} skip\n`;
          });
          setProgress({
            phase: 'cache_complete',
            message: msg
          });
        }
      };

      const summary = await cardsAPI.validate(
        selectedFranchise.schema,
        selectedLanguages,
        translationsByLang,
        cardUuids,
        activeSaleSupports,
        progressCallback
      );

      toast.dismiss('validation');

      if (summary && summary.cardsProcessed !== undefined) {
        toast.success(
          `✅ ${summary.cardsProcessed} cartes traduites avec succès! (${summary.langsProcessed} langues)`,
          {
            autoClose: 5000
          }
        );
      } else {
        throw new Error('La validation n\'a pas retourné de résumé');
      }

      // Réinitialiser
      setTranslationsByLang({});
      setCardUuids([]);
      setCanValidate(false);
      setProgress({ phase: '', current: 0, total: 0, message: '' });
      setSelectedTabLang(null);

    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      toast.dismiss('validation');
      toast.error('Erreur lors de la validation: ' + (error.message || 'Erreur inconnue'), {
        autoClose: 5000
      });
    } finally {
      setValidating(false);
    }
  };

  const handleTranslationsChange = (updatedTranslations) => {
    setTranslationsByLang(prev => ({
      ...prev,
      [selectedTabLang]: updatedTranslations
    }));
  };

  // Calculer les stats totales
  const getTotalStats = () => {
    let totalLocal = 0;
    let totalDeepL = 0;

    Object.values(statsByLang).forEach(stats => {
      totalLocal += stats.localWords || 0;
      totalDeepL += stats.deeplWords || 0;
    });

    return { totalLocal, totalDeepL };
  };

  const { totalLocal, totalDeepL } = getTotalStats();

  return (
    <div className="translation-page">
      <div className="translation-header">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <span>ETK CATALOGUE</span>
          <span className="breadcrumb-separator">/</span>
          <span>TRADUCTION</span>
          {selectedFranchise && (
            <>
              <span className="breadcrumb-separator">/</span>
              <span>{selectedFranchise.label}</span>
            </>
          )}
        </div>

        <h1>Interface de Traduction</h1>

        <div className="translation-controls">
          <div className="control-group">
            <label>Franchise:</label>
            <Select
              options={franchises}
              value={selectedFranchise}
              onChange={setSelectedFranchise}
              placeholder="Sélectionner une franchise..."
              className="select-control"
            />
          </div>

          {/* Cases à cocher pour les langues */}
          <div className="control-group">
            <label>Langues cibles:</label>
            <div className="languages-checkboxes">
              {languages.map(lang => (
                <label key={lang.code} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedLanguages.includes(lang.code)}
                    onChange={() => handleLanguageToggle(lang.code)}
                    disabled={!selectedFranchise || translating || validating}
                  />
                  <span className="checkbox-text">{lang.label}</span>
                </label>
              ))}
              {languages.length === 0 && selectedFranchise && (
                <span className="no-languages">Aucune langue disponible</span>
              )}
              {!selectedFranchise && (
                <span className="no-languages">Sélectionnez d'abord une franchise</span>
              )}
            </div>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleTranslate}
            disabled={!selectedFranchise || selectedLanguages.length === 0 || translating || validating}
          >
            {translating ? 'Traduction en cours...' : 'Traduire'}
          </button>

          <button
            className="btn btn-success"
            onClick={handleValidate}
            disabled={!canValidate || validating}
          >
            {validating ? 'Validation en cours...' : 'Valider les traductions'}
          </button>
        </div>

        {/* Progression */}
        {(translating || validating) && progress.message && (
          <div className="progress-section">
            <div className="progress-message">{progress.message}</div>
            {progress.total > 0 && (
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                ></div>
              </div>
            )}
          </div>
        )}

        {/* Statistiques totales */}
        {(totalLocal > 0 || totalDeepL > 0) && (
          <div className="stats-section">
            <h3>Statistiques globales</h3>
            <div className="stats-grid">
              <div className="stat-card stat-local">
                <div className="stat-label">Mots en cache local</div>
                <div className="stat-value">{totalLocal}</div>
              </div>
              <div className="stat-card stat-deepl">
                <div className="stat-label">Mots via DeepL</div>
                <div className="stat-value">{totalDeepL}</div>
              </div>
              <div className="stat-card stat-total">
                <div className="stat-label">Total</div>
                <div className="stat-value">{totalLocal + totalDeepL}</div>
              </div>
              <div className="stat-card stat-percentage">
                <div className="stat-label">Économie (cache)</div>
                <div className="stat-value">
                  {totalLocal + totalDeepL > 0
                    ? Math.round((totalLocal / (totalLocal + totalDeepL)) * 100)
                    : 0}%
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info */}
        {Object.keys(translationsByLang).length > 0 && (
          <div className="info-section">
            <strong>{selectedLanguages.length}</strong> langue(s) -
            <strong> {cardUuids.length}</strong> carte(s) -
            <strong> {activeSaleSupports.length}</strong> support(s) de vente
          </div>
        )}
      </div>

      {/* Table des traductions avec tabs par langue */}
      <div className="translation-content">
        {Object.keys(translationsByLang).length > 0 ? (
          <>
            {/* Tabs pour les langues */}
            <div className="lang-tabs">
              {selectedLanguages.map(lang => (
                <button
                  key={lang}
                  className={`lang-tab ${selectedTabLang === lang ? 'active' : ''}`}
                  onClick={() => setSelectedTabLang(lang)}
                >
                  {lang.toUpperCase()}
                  <span className="tab-badge">{translationsByLang[lang]?.length || 0}</span>
                </button>
              ))}
            </div>

            {/* Table pour la langue sélectionnée */}
            {selectedTabLang && translationsByLang[selectedTabLang] && (
              <TranslationTable
                translations={translationsByLang[selectedTabLang]}
                onTranslationsChange={handleTranslationsChange}
              />
            )}
          </>
        ) : (
          <div className="empty-state">
            <p>Sélectionnez une franchise et une ou plusieurs langues, puis cliquez sur "Traduire" pour commencer</p>
          </div>
        )}
      </div>

      {/* Dialogue de confirmation personnalisé */}
      {showValidationDialog && (
        <div className="custom-dialog-overlay" onClick={handleCancelValidation}>
          <div className="custom-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="custom-dialog-header">
              <h3>Confirmer la validation</h3>
            </div>
            <div className="custom-dialog-body">
              <p>Voulez-vous valider et appliquer ces traductions aux cartes?</p>
              <div className="custom-dialog-info">
                <p><strong>Langues:</strong> {selectedLanguages.join(', ').toUpperCase()}</p>
                <p><strong>Cartes:</strong> {cardUuids.length}</p>
                <p><strong>Expressions:</strong> {Object.values(translationsByLang).reduce((sum, arr) => sum + arr.length, 0)}</p>
              </div>
              <p className="custom-dialog-warning">
                Cette action mettra à jour les cartes dans la base de données.
              </p>
            </div>
            <div className="custom-dialog-footer">
              <button className="btn-cancel" onClick={handleCancelValidation}>
                Annuler
              </button>
              <button className="btn-confirm" onClick={handleConfirmValidation}>
                Valider
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslationPage;
